import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

import { Bell, ChevronDown, LogOut, User } from 'lucide-react';

import api from '../services/api';
import NotificationDropdown from '../shared/components/notifications/NotificationDropdown';
import ExalogicLogo from '../shared/components/ui/ExalogicLogo';
import UserAvatar from '../shared/components/ui/UserAvatar';
import './Topbar.css';
import * as notificationService from '../features/chat/services/notificationService';
import { useRealTimeChat } from '../features/chat/context/RealTimeChatContext';
import { LanguageSwitcher } from '../features/translation';


const Topbar = ({ user, globalSearch, setGlobalSearch }) => {
    const navigate = useNavigate();


    const { lastMessage } = useRealTimeChat();
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [isVisible, setIsVisible] = useState(true);
    const [lastScrollY, setLastScrollY] = useState(0);

    const dropdownRef = useRef(null);
    const notificationRef = useRef(null);

    // Stable user object
    const currentUser = useMemo(() => user || {
        full_name: 'Jacob Shahjan',
        email: 'jacob.shahjan@company.com'
    }, [user]);

    const displayName = currentUser.employee_name || currentUser.full_name || `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'Jacob Shahjan';
    const displayEmail = currentUser.email || 'jacob.shahjan@company.com';

    const getInitials = (name) => {
        if (!name) return '??';
        return name
            .split(' ')
            .filter(Boolean)
            .map(n => n[0])
            .join('')
            .toUpperCase()
            .substring(0, 2);
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
            if (notificationRef.current && !notificationRef.current.contains(event.target)) {
                setIsNotificationsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Handle scroll for contextual topbar visibility
    useEffect(() => {
        const handleScroll = () => {
            const currentScrollY = window.scrollY;

            // Show topbar if scrolling up, hide if scrolling down
            // But always show if we are near the very top (e.g. < 50px)
            if (currentScrollY < 50) {
                setIsVisible(true);
            } else if (currentScrollY > lastScrollY && currentScrollY > 100) {
                setIsVisible(false); // Scrolling down
            } else if (currentScrollY < lastScrollY) {
                setIsVisible(true); // Scrolling up
            }

            setLastScrollY(currentScrollY);
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [lastScrollY]);

    // Fetch on mount
    useEffect(() => {
        fetchUnreadCount();
        fetchNotifications(true);
    }, []);


    // Real-time notification updates
    useEffect(() => {
        if (lastMessage && (lastMessage.type === 'NOTIFICATION_RECEIVED' || lastMessage.type === 'RFP_ASSIGNED')) {
            fetchUnreadCount();
            if (isNotificationsOpen) {
                fetchNotifications(true);
            }
        }
    }, [lastMessage, isNotificationsOpen]);

    const fetchUnreadCount = async () => {
        try {
            const response = await notificationService.getUnreadNotificationCount();
            if (response.status === 200 || response.status === 201) {
                // Robust check for different possible backend formats
                const count = response.data?.unread_count ??
                    response.data?.count ??
                    (typeof response.data === 'number' ? response.data : 0);

                setUnreadCount(count);
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
            setUnreadCount(0);
        }
    };


    const fetchNotifications = async (silent = false) => {
        if (!silent) setLoading(true);
        try {
            const response = await notificationService.listNotifications({ limit: 50 });
            if (response.status === 200) {
                // Backend might return array directly or { notifications: [] }
                const data = Array.isArray(response.data)
                    ? response.data
                    : (response.data?.notifications || []);
                setNotifications(data);
            }
        } catch (error) {

            console.error('Error fetching notifications:', error);
        } finally {
            if (!silent) setLoading(false);
        }
    };

    const handleNotificationToggle = () => {
        const newState = !isNotificationsOpen;
        setIsNotificationsOpen(newState);
        if (newState) {
            fetchNotifications();
            setIsDropdownOpen(false); // Close profile dropdown if open
        }
    };

    const handleNotificationClick = async (notification) => {
        // 1. Close notification dropdown immediately
        setIsNotificationsOpen(false);

        // 2. Navigate to link if it exists
        if (notification.link) {
            navigate(notification.link);
        }

        // 3. Mark as read on the backend (if not already read)
        if (notification.read_status === false || notification.is_read === false) {
            try {
                const response = await notificationService.markNotificationAsRead(notification._id || notification.id);

                if (response.status === 200 || response.status === 201) {
                    // Update local state
                    setNotifications(prev => prev.map(n =>
                        (n._id === notification._id || n.id === notification.id) ? { ...n, read_status: true, is_read: true } : n
                    ));
                    setUnreadCount(prev => Math.max(0, prev - 1));
                }
            } catch (error) {
                console.error('Error marking notification as read:', error);
            }
        }
    };

    const handleMarkAllRead = async () => {
        const unreadNotifications = notifications.filter(n => !n.read_status && !n.is_read);
        if (unreadNotifications.length === 0) return;

        try {
            const response = await notificationService.markAllNotificationsAsRead();
            if (response.status === 200) {
                // Update all to read locally
                setNotifications(prev => prev.map(n => ({ ...n, read_status: true, is_read: true })));
                setUnreadCount(0);
            }
        } catch (error) {
            console.error('Error marking all as read:', error);
        }
    };



    const handleLogout = async () => {
        try {
            await api.post('/rfp/auth/logout');
        } catch (error) {
            console.error('Logout API error:', error);
        } finally {
            // Always clear storage and navigate even if API fails
            localStorage.removeItem('access_token');
            localStorage.removeItem('user');
            sessionStorage.clear();
            window.location.href = '/login?reason=logout';
        }
    };

    return (
        <header className={`topbar ${!isVisible ? 'topbar-hidden' : ''}`}>
            <div className="topbar-left">
                <div className="logo-section">
                    <ExalogicLogo theme="light" />
                </div>
            </div>

            <div className="search-container">
            </div>

            <div className="topbar-actions">
                <LanguageSwitcher placement="topbar" />

                <div className="notification-container" ref={notificationRef}>
                    <div
                        className={`notification-badge ${isNotificationsOpen ? 'active' : ''}`}
                        onClick={handleNotificationToggle}
                    >
                        <Bell size={20} className="action-icon" />
                        {unreadCount > 0 && <span className="badge-count">{unreadCount}</span>}
                    </div>

                    {isNotificationsOpen && (
                        <NotificationDropdown
                            notifications={notifications}
                            loading={loading}
                            onMarkAllRead={handleMarkAllRead}
                            onNotificationClick={handleNotificationClick}
                            onClose={() => setIsNotificationsOpen(false)}
                        />
                    )}
                </div>

                <div className="user-dropdown-container" ref={dropdownRef}>
                    <div
                        className={`user-trigger ${isDropdownOpen ? 'active' : ''}`}
                        onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    >
                        <UserAvatar className="small-avatar" imageUrl={currentUser.avatar_url} initials={getInitials(displayName)} />
                        <ChevronDown size={14} className={`chevron ${isDropdownOpen ? 'rotate' : ''}`} />
                    </div>

                    {isDropdownOpen && (
                        <div className="profile-dropdown-menu">
                            <div className="menu-header">
                                <div className="user-name">{displayName}</div>
                                <div className="user-email">{displayEmail}</div>
                            </div>

                            <div className="menu-divider"></div>

                            <div className="menu-items">
                                <button
                                    className="menu-item"
                                    onClick={() => {
                                        setIsDropdownOpen(false);
                                        navigate('/profile');
                                    }}
                                >
                                    <User size={16} />
                                    <span>My Profile</span>
                                </button>

                                {/*
                                <button className="menu-item" onClick={() => setIsDropdownOpen(false)}>
                                    <Settings size={16} />
                                    <span>Settings</span>
                                </button>
                                <button className="menu-item" onClick={() => setIsDropdownOpen(false)}>
                                    <Bell size={16} />
                                    <span>Notifications</span>
                                </button>

                                <div className="menu-divider"></div>
                                */}

                                <button className="menu-item logout-item" onClick={handleLogout}>
                                    <LogOut size={16} />
                                    <span>Log Out</span>
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </header>
    );
};

export default Topbar;
